import { parsePgArray } from "@/lib/pgArray";
import { customFieldValues } from "@/lib/customFields";
import type { BacklogItem } from "@/types/backlog";

/**
 * A ticket as it comes off the wire, before normalisation.
 *
 * Identical to BacklogItem except that the four array columns are `unknown`:
 * PostgREST hands them back as a JS array, as a Postgres literal (`{a,b}`), or
 * as null depending on the query, which is the whole reason this function
 * exists. Everything else is already the right shape and stays typed.
 *
 * team_ids belongs in this list even though the column is NOT NULL: what varies
 * is the wire FORMAT, not the nullability, and a realtime payload delivers it as
 * a literal string exactly like the other three. assigned_team_id is absent on
 * purpose — it is a scalar uuid and needs no parsing.
 */
export type RawBacklogItem = Omit<
  BacklogItem,
  "assigned_to_id" | "attachments" | "subsheet_ids" | "team_ids" | "custom_fields"
> & {
  assigned_to_id?: unknown;
  attachments?: unknown;
  subsheet_ids?: unknown;
  team_ids?: unknown;
  /** jsonb, so an object off the wire; anything else reads as no values (#9399). */
  custom_fields?: unknown;
};

/**
 * Narrows an untyped realtime row to a ticket row.
 *
 * The change stream carries whatever Postgres put in the NOTIFY payload, which
 * TypeScript can only see as Record<string, unknown>. Everything downstream of
 * this cast is typed, so the unsafe step is one line in one place instead of
 * being re-asserted at every call site.
 */
export const asBacklogRow = (row: Record<string, unknown>): RawBacklogItem => row as unknown as RawBacklogItem;

/**
 * Normalize array fields from PostgreSQL format to proper JS arrays.
 * Uses the centralized parsePgArray utility to handle all edge cases.
 */
export const normalizeBacklogItem = (item: RawBacklogItem): BacklogItem => {
  const assignedToIds = parsePgArray(item.assigned_to_id);
  const attachments = parsePgArray(item.attachments);
  const subsheetIds = parsePgArray(item.subsheet_ids);
  const teamIds = parsePgArray(item.team_ids);

  return {
    ...item,
    assigned_to_id: assignedToIds.length > 0 ? assignedToIds : null,
    attachments: attachments.length > 0 ? attachments : null,
    subsheet_ids: subsheetIds.length > 0 ? subsheetIds : null,
    // Empty stays an empty array rather than becoming null, unlike the three
    // above: the column is NOT NULL, so a consumer may map over it without a
    // guard, and turning it into null here would reintroduce exactly the
    // optional-chaining the NOT NULL default removed.
    team_ids: teamIds,
    // Normalised once here so every reader can index it (#9399).
    custom_fields: customFieldValues(item.custom_fields),
  };
};

/**
 * A ticket's assignees resolved to profiles, IN THE TICKET'S OWN ORDER.
 *
 * The order is the model: `assigned_to_id[0]` is the primary assignee, and
 * every surface that draws a stack, a name or a sort key reads that position.
 * Resolving with `profiles.filter(p => ids.includes(p.id))` — which is what
 * four call sites did — silently re-sorts the people into PROFILE order, so
 * which face led the stack depended on how the profiles query came back. Map
 * over the ids instead; that is the whole reason this exists.
 *
 * An id with no profile (deactivated, or a stale cache) is dropped rather than
 * rendered as a blank face.
 */
export const orderedAssignees = <T extends { id: string }>(assignedToId: unknown, profiles: T[]): T[] => {
  const ids = parsePgArray(assignedToId);
  if (ids.length === 0 || profiles.length === 0) return [];
  const byId = new Map(profiles.map((p) => [p.id, p]));
  return ids.map((id) => byId.get(id)).filter((p): p is T => Boolean(p));
};

/**
 * Move `id` to the front of an ordered membership list — "make this one the
 * primary". Adds it if it was not a member, which is what makes the same
 * action work from a picker row that has not been selected yet.
 *
 * A new array every time, never a splice: the lists it operates on are React
 * state and query-cache values.
 */
export const promoteToPrimary = (ids: string[], id: string): string[] => [id, ...ids.filter((x) => x !== id)];

/**
 * The first of a ticket's departments that this viewer can see — the one to
 * group it under or draw its tile for.
 *
 * Not simply `subsheet_ids[0]`: a department-restricted person (#7470) can read
 * a ticket that is also in a department they cannot see, and that department
 * may be first. Keying on it would file the ticket under a group that never
 * renders, and the ticket would vanish from their list. `null` when none of
 * them is visible, which only happens for a ticket they reach by being
 * involved in it rather than through a department.
 */
export const firstVisibleSubsheetId = (
  subsheetIds: readonly string[] | null | undefined,
  visible: { has(id: string): boolean },
): string | null => subsheetIds?.find((id) => visible.has(id)) ?? null;
